import { z } from "zod";
import { defineStepConfig } from "../registry/helpers";

const changedFileSchema = z.object({
  fileName: z.string().describe("Name of the changed file"),
  filePath: z.string().describe("Full blob key/path of the changed file"),
  containerName: z.string().describe("Storage container the file resides in"),
  contentType: z.string().describe("Detected MIME type"),
  fileSize: z.number().describe("File size in bytes"),
  lastModified: z.string().describe("ISO timestamp of last modification"),
});

/**
 * Watch for File Changes Trigger
 *
 * Detects new or modified files in a storage location and outputs them
 * for downstream fanout processing.
 *
 * Change detection strategies:
 *   - event:            Blob store pushes events (Azure Event Grid / S3 notifications).
 *                        Only available for blob storage, not SFTP.
 *                        Cron is optional as a reconciliation/catch-up sweep.
 *   - cursor:           Poll on cron, track {key, etag, mtime} between runs.
 *                        Works for SFTP and blob stores without events.
 *   - move_after_fetch: No state — anything in the source folder is "new".
 *                        Downstream steps move processed files to an archive prefix.
 *                        Works for SFTP and blob stores.
 *
 * Uses the same storage config pattern as blob_storage_fetch (container,
 * prefix, regex, folder discovery, placeholders).
 */
export const watchForFileChangesStep = defineStepConfig({
  type: "watch_for_file_changes",
  name: "Watch for File Changes",
  description:
    "Detects new or modified files in blob storage or SFTP and outputs changed files for downstream processing.",
  category: "Trigger",

  inputSchema: z.never(),

  outputSchema: z.object({
    changedFiles: z
      .array(changedFileSchema)
      .describe("Files that are new or modified since the last poll"),
    totalFound: z
      .number()
      .describe("Total number of files matching the pattern"),
    changedCount: z
      .number()
      .describe("Number of files that changed since last poll"),
    pollTimestamp: z
      .string()
      .describe("ISO timestamp of this poll (used as cursor for next run)"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile used (propagated to downstream steps)"),
  }),

  configSchema: z.object({
    // ── Change Detection ──
    changeDetectionStrategy: z
      .enum(["event", "cursor", "move_after_fetch"])
      .default("move_after_fetch")
      .describe(
        "How to detect new files. " +
          "'event': blob store events (Azure Event Grid / S3) — blob only, not SFTP. " +
          "'cursor': poll + track seen files between runs — works everywhere. " +
          "'move_after_fetch': anything in source folder is new, downstream moves processed files — works everywhere.",
      ),
    moveAfterFetchPrefix: z
      .string()
      .optional()
      .describe(
        "Destination prefix for processed files (e.g., 'processed/' or 'archive/{{YYYYMMDD}}/''). " +
          "Only used when changeDetectionStrategy is 'move_after_fetch'.",
      ),

    // ── Storage ──
    storageType: z
      .enum(["blob", "sftp"])
      .default("blob")
      .describe(
        "Storage backend. 'blob' = Azure Blob / S3. 'sftp' = SFTP server. " +
          "When 'sftp', changeDetectionStrategy 'event' is not available.",
      ),
    containerName: z
      .string()
      .optional()
      .describe(
        "Storage container name (blob) or base path (SFTP). Defaults to system default.",
      ),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe(
        "Which storage account to use. 'ship_and_debit' uses PROD_AZURE_STORAGE_CONNECTION_STRING. Blob only.",
      ),

    // ── File Matching ──
    blobKeyPattern: z
      .string()
      .optional()
      .describe(
        "Prefix to list files. Placeholders: {YYYY} {YYYYMM} {YYYYMMDD} {HHmm} {HHmmss} (resolved at runtime).",
      ),
    blobKeyFilterRegex: z
      .string()
      .optional()
      .describe(
        "Optional regex to filter listed keys. Same placeholders supported.",
      ),
    folderPrefixes: z
      .array(z.string())
      .optional()
      .describe(
        "List of folder prefixes to scan for multi-folder discovery. Placeholders supported.",
      ),
    fileSelectionStrategy: z
      .enum([
        "most_recent",
        "oldest",
        "exact",
        "most_recent_by_name",
        "oldest_by_name",
      ])
      .prefault("most_recent")
      .describe("When multiple files match, how to sort candidates."),
    placeholderTimezone: z
      .string()
      .optional()
      .describe(
        "IANA timezone for resolving date/time placeholders. Defaults to America/New_York.",
      ),

    // ── Schedule ──
    cronExpression: z
      .string()
      .default("0 * * * *")
      .describe(
        "Cron expression for polling / reconciliation. " +
          "For 'event' strategy this is an optional catch-up sweep. " +
          "For 'cursor' and 'move_after_fetch' this is the primary polling interval.",
      ),
    timezone: z
      .string()
      .default("UTC")
      .describe("IANA timezone for the cron schedule"),
    overlapPolicy: z
      .enum(["skip", "buffer_one", "allow_all", "cancel_other"])
      .default("skip")
      .describe("What to do if previous run is still executing"),
    failIfNoneFound: z
      .boolean()
      .prefault(false)
      .optional()
      .describe("Cancel workflow if no changed files are found"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 3,
      backoffCoefficient: 2,
      initialInterval: "10 seconds",
    },
  },
});

export type WatchForFileChangesConfig = z.infer<
  typeof watchForFileChangesStep.configSchema
>;
